import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * 翻译模型选择器组件
 * 提供翻译模型选择的用户界面，支持卡片和下拉两种样式。
 *
 * @example
 * ```tsx
 * // 卡片样式 (默认)
 * <ModelSelector
 *   variant="cards"
 *   value={selectedModel}
 *   onChange={setSelectedModel}
 *   models={availableModels}
 * />
 *
 * // 下拉样式
 * <ModelSelector
 *   variant="dropdown"
 *   value={selectedModel}
 *   onChange={setSelectedModel}
 *   models={availableModels}
 * />
 * ```
 */
import { useMemo, Fragment } from 'react';
import { Listbox, Transition } from '@headlessui/react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
// --- CVA 定义 ---
// 卡片样式
const modelCardVariants = cva('py-2 px-3 rounded-lg text-xs font-medium transition-colors border', {
    variants: {
        selected: {
            true: 'bg-interactive-emerald-flash/20 text-interactive-emerald-flash border-interactive-emerald-flash',
            false: 'bg-gray-100 text-stone-gray border-gray-300 hover:bg-gray-200',
        },
        disabled: {
            true: 'opacity-50 cursor-not-allowed',
        },
    },
    defaultVariants: {
        selected: false,
        disabled: false,
    },
});
// 下拉按钮样式 (参考 LanguageSelector)
const listboxButtonVariants = cva('relative w-full cursor-default rounded-lg border bg-white py-2 pl-3 pr-10 text-left text-xs transition-colors focus:outline-none focus-visible:border-interactive-emerald-flash focus-visible:ring-2 focus-visible:ring-interactive-emerald-flash/50 focus-visible:ring-offset-2', {
    variants: {
        disabled: {
            true: 'cursor-not-allowed bg-gray-100 text-gray-500',
            false: 'hover:border-gray-400',
        },
    },
    defaultVariants: {
        disabled: false,
    },
});
/**
 * 翻译模型选择器组件
 * 支持卡片式和下拉式两种选择界面
 */
export const ModelSelector = ({ value, onChange, models = [], variant, label = 'Select Translation Model', disabled = false, className = '', placeholder = 'Select a model', useCards, }) => {
    // 兼容旧的 useCards 属性
    const finalVariant = variant ?? (useCards === false ? 'dropdown' : 'cards');
    const availableModels = useMemo(() => models.filter(model => model.isAvailable === undefined || model.isAvailable), [models]);
    const selectedModel = useMemo(() => availableModels.find(model => model.id === value), [availableModels, value]);
    // --- 无可用模型时的提示 ---
    if (availableModels.length === 0) {
        return (_jsxs("div", { className: cn('model-selector', className), children: [label && _jsx("label", { className: "block text-xs font-medium text-stone-gray mb-1", children: label }), _jsx("div", { className: "p-4 border border-gray-200 rounded-lg bg-gray-50 text-stone-gray text-center text-xs", children: "No models available" })] }));
    }
    // --- 下拉菜单模式 ---
    if (finalVariant === 'dropdown') {
        return (_jsx("div", { className: cn('w-full', className), children: _jsx(Listbox, { value: value, onChange: onChange, disabled: disabled, children: ({ open }) => (_jsxs("div", { children: [label && (_jsx(Listbox.Label, { className: "block text-xs font-medium text-stone-gray mb-1", children: label })), _jsxs("div", { className: "relative", children: [_jsxs(Listbox.Button, { className: cn(listboxButtonVariants({ disabled }), !open && 'border-gray-300'), children: [_jsx("span", { className: "block truncate", children: selectedModel?.name ?? _jsx("span", { className: "text-gray-400", children: placeholder }) }), _jsx("span", { className: "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2", children: _jsx("svg", { xmlns: "http://www.w3.org/2000/svg", className: "h-5 w-5 text-gray-400", viewBox: "0 0 20 20", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { fillRule: "evenodd", d: "M10 3a.75.75 0 01.53.22l3.5 3.5a.75.75 0 01-1.06 1.06L10 4.81 7.03 7.78a.75.75 0 01-1.06-1.06l3.5-3.5A.75.75 0 0110 3zm-3.72 9.28a.75.75 0 011.06 0L10 15.19l2.97-2.91a.75.75 0 111.06 1.06l-3.5 3.5a.75.75 0 01-1.06 0l-3.5-3.5a.75.75 0 010-1.06z", clipRule: "evenodd" }) }) })] }), _jsx(Transition, { as: Fragment, leave: "transition ease-in duration-100", leaveFrom: "opacity-100", leaveTo: "opacity-0", children: _jsx(Listbox.Options, { className: "absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-xs shadow-lg ring-1 ring-black/5 focus:outline-none", children: availableModels.map(model => (_jsx(Listbox.Option, { className: ({ active }) => cn('relative cursor-default select-none py-2 pl-10 pr-4', active ? 'bg-static-pale-mint text-static-deep-cyan' : 'text-stone-gray'), value: model.id, children: ({ selected }) => (_jsxs(_Fragment, { children: [_jsx("span", { className: cn('block truncate', selected ? 'font-semibold' : 'font-normal'), children: model.name }), selected ? (_jsx("span", { className: "absolute inset-y-0 left-0 flex items-center pl-3 text-interactive-emerald-flash", children: _jsx("svg", { xmlns: "http://www.w3.org/2000/svg", className: "h-5 w-5", viewBox: "0 0 20 20", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { fillRule: "evenodd", d: "M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.052-.143z", clipRule: "evenodd" }) }) })) : null] })) }, model.id))) }) })] })] })) }) }));
    }
    // --- 卡片按钮模式 (默认) ---
    return (_jsxs("div", { className: cn('model-selector', className), children: [label && _jsx("label", { className: "block text-xs font-medium text-stone-gray mb-1", children: label }), _jsx("div", { className: "grid grid-cols-2 gap-2", children: availableModels.map(model => (_jsx("button", { type: "button", className: cn(modelCardVariants({
                        selected: value === model.id,
                        disabled,
                    })), onClick: () => onChange(model.id), disabled: disabled, "aria-pressed": value === model.id, children: model.name }, model.id))) })] }));
};
export default ModelSelector;
//# sourceMappingURL=ModelSelector.js.map